import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, getEvents, deleteEvent as deleteEventFromDb, updateEventStatus } from '../firebase';
import { getApiUrl } from '../config';
import { User } from '../types';

export function useEvents(user: User | null) {
  const [events, setEvents] = useState<any[]>([]);
  const [isEventsLoading, setIsEventsLoading] = useState(false);

  // Load and enrich events with patient and user data
  const loadAndEnrichEvents = useCallback(async () => {
    if (!user) return [];
    
    try {
      const eventsData = await getEvents();
      
      // Enrich events with patient and user data
      const enrichedEvents = await Promise.all(eventsData.map(async (event) => {
        let enrichedEvent = { ...event };
        
        // Get patient data if caseId exists
        if (event.caseId) {
          try {
            const piiResponse = await fetch(getApiUrl(`/api/patients/${event.caseId}`));
            if (piiResponse.ok) {
              const piiData = await piiResponse.json();
              enrichedEvent.patient = {
                firstName: piiData.patient.first_name,
                lastName: piiData.patient.last_name,
                email: piiData.patient.email,
                phone: piiData.patient.phone,
                address: piiData.patient.address,
                notes: piiData.patient.notes
              };
            }
          } catch (error) {
            console.log('Could not fetch patient data for caseId:', event.caseId, error);
          }
        }
        
        // Get user data for createdBy
        if (event.createdBy) {
          try {
            const userDocRef = doc(db, 'users', event.createdBy);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              const userData = userDoc.data() as any;
              enrichedEvent.createdByName = userData.name || userData.email || 'Unknown User';
            }
          } catch (error) {
            console.log('Could not fetch user data for createdBy:', event.createdBy, error);
          }
        }
        
        return enrichedEvent;
      }));
      
      return enrichedEvents;
    } catch (error) {
      console.error('Error loading and enriching events:', error);
      return [];
    }
  }, [user]);

  // Load events on mount
  useEffect(() => {
    const loadEvents = async () => {
      if (!user) return;
      
      setIsEventsLoading(true);
      try {
        const enrichedEvents = await loadAndEnrichEvents();
        setEvents(enrichedEvents);
      } catch (error) {
        console.error('Error loading events:', error);
      } finally {
        setIsEventsLoading(false);
      }
    };

    loadEvents();
  }, [user, loadAndEnrichEvents]);

  const handleEventStatusChange = useCallback(async (eventId: string, status: 'active' | 'completed' | 'cancelled') => {
    try {
      await updateEventStatus(eventId, status, user?.id || '');
      const enrichedEvents = await loadAndEnrichEvents();
      setEvents(enrichedEvents);
    } catch (error) {
      console.error('Error updating event status:', error);
    }
  }, [user?.id, loadAndEnrichEvents]);

  const handleDeleteEvent = useCallback(async (eventId: string) => {
    try {
      await deleteEventFromDb(eventId, user?.id || '');
      const eventsData = await getEvents();
      setEvents(eventsData);
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  }, [user?.id]);

  const handleArchiveEvent = useCallback(async (eventId: string, archived: boolean) => {
    try {
      const eventRef = doc(db, 'events', eventId);
      await updateDoc(eventRef, { archived });
      
      // Also archive/unarchive the corresponding activity (meeting)
      const { collection, query, where, getDocs, updateDoc: updateActivityDoc } = await import('firebase/firestore');
      const activitiesRef = collection(db, 'activities');
      const activitiesQuery = query(activitiesRef, where('eventId', '==', eventId));
      const activitiesSnapshot = await getDocs(activitiesQuery);
      
      // Update all activities linked to this event
      const updatePromises = activitiesSnapshot.docs.map(async (activityDoc) => {
        await updateActivityDoc(activityDoc.ref, { 
          archived,
          updatedAt: (await import('firebase/firestore')).serverTimestamp()
        });
      });
      
      await Promise.all(updatePromises);
      
      const enrichedEvents = await loadAndEnrichEvents();
      setEvents(enrichedEvents);
    } catch (error) {
      console.error(`Error ${archived ? 'archiving' : 'unarchiving'} event:`, error);
    }
  }, [loadAndEnrichEvents]);

  return {
    events,
    setEvents,
    isEventsLoading,
    handleEventStatusChange,
    handleDeleteEvent,
    handleArchiveEvent,
    loadAndEnrichEvents
  };
}

